/**
 * Calcule la distance entre deux adresses en utilisant OpenStreetMap (Nominatim)
 * et la formule de Haversine pour calculer la distance à vol d'oiseau.
 * Puis applique un coefficient de 1.3 pour estimer la distance routière.
 */

interface Coordinates {
  lat: number;
  lon: number;
}

/**
 * Géocode une adresse en utilisant l'API Nominatim d'OpenStreetMap
 */
const geocodeAddress = async (address: string): Promise<Coordinates | null> => {
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
      {
        headers: {
          'User-Agent': 'GaragePayerne/1.0',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Erreur lors de la géolocalisation');
    }

    const data = await response.json();

    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon),
      };
    }

    return null;
  } catch (error) {
    console.error('Erreur géocodage:', error);
    return null;
  }
};

/**
 * Calcule la distance entre deux points géographiques en utilisant la formule de Haversine
 * Retourne la distance en mètres
 */
const calculateHaversineDistance = (
  coord1: Coordinates,
  coord2: Coordinates
): number => {
  const R = 6371000; // Rayon de la Terre en mètres
  const lat1 = (coord1.lat * Math.PI) / 180;
  const lat2 = (coord2.lat * Math.PI) / 180;
  const deltaLat = ((coord2.lat - coord1.lat) * Math.PI) / 180;
  const deltaLon = ((coord2.lon - coord1.lon) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

/**
 * Calcule la distance routière entre deux adresses
 * Utilise un coefficient de 1.3 pour convertir la distance à vol d'oiseau en distance routière
 */
export const calculateDistance = async (
  origin: string,
  destination: string
): Promise<{ success: boolean; distance?: number; error?: string }> => {
  try {
    // Géocoder l'origine
    const originCoords = await geocodeAddress(origin);
    if (!originCoords) {
      return {
        success: false,
        error: 'Impossible de localiser l\'adresse de départ',
      };
    }

    // Essayer différents formats d'adresse pour la destination
    const addressFormats = [
      destination,
      `${destination}, Switzerland`,
      `${destination}, Suisse`,
    ];

    let destCoords: Coordinates | null = null;

    for (const testAddress of addressFormats) {
      destCoords = await geocodeAddress(testAddress);
      if (destCoords) break;
      
      // Attendre un peu entre les requêtes pour respecter les limites de l'API
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    if (!destCoords) {
      return {
        success: false,
        error: 'Adresse introuvable. Vérifiez votre saisie.',
      };
    }

    // Calculer la distance à vol d'oiseau
    const straightDistance = calculateHaversineDistance(originCoords, destCoords);

    // Appliquer un coefficient de 1.3 pour estimer la distance routière
    // (les routes ne sont jamais parfaitement droites)
    const roadDistance = Math.round(straightDistance * 1.3);

    return {
      success: true,
      distance: roadDistance,
    };
  } catch (error) {
    console.error('Erreur calcul distance:', error);
    return {
      success: false,
      error: 'Erreur lors du calcul de la distance.',
    };
  }
};
